import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { CreditCard, Check, ArrowRight, ExternalLink, Loader2, AlertCircle } from 'lucide-react';
import { motion } from 'motion/react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/components/ui/toast';
import { useProjects } from '@/hooks';
import { usePlanLimits } from '@/hooks/usePlanLimits';
import { apiClient } from '@/api/client';
import { formatLimit, isUnlimited, type ResourceUsage } from '@/types/billing';

interface PlanFeature {
  name: string;
  free: string;
  pro: string;
}

const PLAN_FEATURES: PlanFeature[] = [
  { name: 'Projects', free: '3', pro: 'Unlimited' },
  { name: 'Secrets per Environment', free: '100', pro: 'Unlimited' },
  { name: 'Team Members', free: '1', pro: 'Unlimited' },
  { name: 'Environments per Project', free: '3', pro: 'Unlimited' },
  { name: 'Audit Logs', free: '7 days', pro: 'Unlimited' },
  { name: 'Priority Support', free: '-', pro: 'Included' },
];

const PRO_PRICE = 12;

function UsageBar({ label, usage }: { label: string; usage: ResourceUsage }) {
  const limitText = isUnlimited(usage.limit)
    ? 'Unlimited'
    : `${usage.current} / ${formatLimit(usage.limit)}`;
  const percentage = isUnlimited(usage.limit) ? 0 : Math.min(usage.percentage, 100);
  const isNearLimit = percentage >= 80 && !isUnlimited(usage.limit);
  const isAtLimit = usage.current >= usage.limit && !isUnlimited(usage.limit);

  return (
    <div className="space-y-2">
      <div className="flex justify-between text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span
          className={
            isAtLimit
              ? 'text-destructive font-medium'
              : isNearLimit
                ? 'text-warning font-medium'
                : 'text-foreground'
          }
        >
          {limitText}
        </span>
      </div>
      {!isUnlimited(usage.limit) && (
        <Progress
          value={percentage}
          className={`h-2 ${isAtLimit ? '[&>div]:bg-destructive' : isNearLimit ? '[&>div]:bg-warning' : ''}`}
        />
      )}
    </div>
  );
}

export function BillingPage() {
  const toast = useToast();
  const { primaryTeamId, isLoadingProjects } = useProjects();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { usage, isLoading: isLoadingUsage, error: usageError } = usePlanLimits(primaryTeamId);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);
  const hasHandledSuccess = useRef(false);

  // Handle Stripe checkout success redirect
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId && location.pathname.includes('/billing/success') && !hasHandledSuccess.current) {
      hasHandledSuccess.current = true;
      toast.success('Upgrade successful! Your plan has been updated.');
      // Clean up the URL
      navigate('/billing', { replace: true });
    }
  }, [searchParams, location.pathname, toast, navigate]);

  const isPro = usage?.plan === 'pro';
  const isEnterprise = usage?.plan === 'enterprise';

  const handleUpgrade = async () => {
    if (!primaryTeamId) return;
    setIsUpgrading(true);
    try {
      const response = await apiClient.post<{ checkout_url: string }>(`/api/v1/teams/${primaryTeamId}/checkout`);
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (err) {
      console.error('Failed to create checkout session:', err);
      toast.error('Failed to start upgrade process. Please try again.');
    } finally {
      setIsUpgrading(false);
    }
  };

  const handleManageBilling = async () => {
    if (!primaryTeamId) return;
    setIsOpeningPortal(true);
    try {
      const response = await apiClient.post<{ portal_url: string }>(`/api/v1/teams/${primaryTeamId}/portal`);
      // Open Stripe billing portal in new tab
      window.open(response.data.portal_url, '_blank');
    } catch (err) {
      console.error('Failed to open billing portal:', err);
      toast.error('Failed to open billing portal. Please try again.');
    } finally {
      setIsOpeningPortal(false);
    }
  };

  if (isLoadingProjects || isLoadingUsage) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!primaryTeamId) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-2 text-destructive">
          <AlertCircle className="h-5 w-5" />
          <span>No team found</span>
        </div>
      </div>
    );
  }

  if (usageError) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-2 text-destructive">
          <AlertCircle className="h-5 w-5" />
          <span>Failed to load billing information</span>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="p-6 space-y-6 max-w-6xl mx-auto"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-primary/10">
          <CreditCard className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold">Billing</h1>
          <p className="text-muted-foreground">Manage your subscription and usage</p>
        </div>
      </div>

      {/* Current Plan */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Current Plan</CardTitle>
              <CardDescription>Your team&apos;s subscription details</CardDescription>
            </div>
            <Badge variant={isPro || isEnterprise ? 'default' : 'outline'} className="text-sm px-3 py-1">
              {isPro ? 'Pro' : isEnterprise ? 'Enterprise' : 'Free'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Usage Stats */}
          {usage && (
            <div className="grid gap-4 sm:grid-cols-2">
              <UsageBar label="Projects" usage={usage.projects} />
              <UsageBar label="Secrets per Environment" usage={usage.secrets} />
              <UsageBar label="Team Members" usage={usage.members} />
              <UsageBar label="Environments per Project" usage={usage.environments} />
            </div>
          )}
        </CardContent>
        <CardFooter className="bg-muted/50 border-t">
          {isPro || isEnterprise ? (
            <Button variant="outline" onClick={handleManageBilling} disabled={isOpeningPortal}>
              {isOpeningPortal ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <ExternalLink className="h-4 w-4 mr-2" />
              )}
              Manage Subscription
            </Button>
          ) : (
            <div className="text-sm text-muted-foreground">
              Upgrade to Pro to unlock unlimited resources
            </div>
          )}
        </CardFooter>
      </Card>

      {/* Plan Comparison */}
      {!isPro && !isEnterprise && (
        <div className="grid gap-6 md:grid-cols-2">
          {/* Free Plan */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                Free
                <Badge variant="outline">Current</Badge>
              </CardTitle>
              <CardDescription>For individuals getting started</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold mb-6">
                $0
                <span className="text-sm font-normal text-muted-foreground">/month</span>
              </div>
              <ul className="space-y-3">
                {PLAN_FEATURES.map((feature) => (
                  <li key={feature.name} className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-muted-foreground" />
                    <span>
                      {feature.name}: <span className="font-medium">{feature.free}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Pro Plan */}
          <Card className="border-primary relative overflow-hidden">
            <div className="absolute top-0 right-0 bg-primary text-primary-foreground text-xs font-medium px-3 py-1 rounded-bl-lg">
              Recommended
            </div>
            <CardHeader>
              <CardTitle>Pro</CardTitle>
              <CardDescription>For growing teams</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold mb-6">
                ${PRO_PRICE}
                <span className="text-sm font-normal text-muted-foreground">/month</span>
              </div>
              <ul className="space-y-3">
                {PLAN_FEATURES.map((feature) => (
                  <li key={feature.name} className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-primary" />
                    <span>
                      {feature.name}: <span className="font-medium">{feature.pro}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </CardContent>
            <CardFooter>
              <Button className="w-full" onClick={handleUpgrade} disabled={isUpgrading}>
                {isUpgrading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <ArrowRight className="h-4 w-4 mr-2" />
                )}
                Upgrade to Pro
              </Button>
            </CardFooter>
          </Card>
        </div>
      )}

      {/* Enterprise Contact */}
      {!isEnterprise && (
        <Card>
          <CardHeader>
            <CardTitle>Need more?</CardTitle>
            <CardDescription>
              For larger organizations with custom requirements
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Enterprise plans include SSO, advanced audit logs, dedicated support, and custom integrations.
            </p>
            <Button variant="outline" asChild>
              <a href="mailto:sales@keyenv.dev">Contact Sales</a>
            </Button>
          </CardContent>
        </Card>
      )}
    </motion.div>
  );
}
